import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { hasNativePlugin, isNative } from './is-native';
import type { VoiceMessageDelivery } from '@/lib/voice/message-delivery';

export interface VoiceProofSnapshot {
  runtimeId?: string;
  isSpeaking?: boolean;
  surface?: 'chat-v2' | '';
  currentTurnId?: string;
  thinking?: boolean;
  state: 'idle' | 'connecting' | 'active' | 'ended';
  engine?: 'elevenlabs' | 'own-brain';
  muted: boolean;
  providerCallId?: string;
  conversationId?: string;
  accountId?: string;
  userAuthId?: string;
  reason?: string;
  /** The line dropped and the call is holding on for it (TKT-1462). Absent on older binaries. */
  reconnecting?: boolean;
  /** Times this call has picked itself back up. */
  reconnects?: number;
  events: { seq: number; kind: string; text: string; at?: number; payload?: Record<string, unknown>; visible?: boolean }[];
}
export interface VoiceProofStart {
  conversationToken: string;
  engine: 'elevenlabs' | 'own-brain';
  conversationId: string;
  accountId: string;
  userAuthId: string;
  dynamicVariables: Record<string, string>;
  overrides: { agent: { prompt: { prompt: string }; firstMessage: string }; tts?: { voiceId: string } };
  brainSessionId?: string;
  controlToken?: string;
  surface?: 'chat-v2';
  nativeControlSessionId?: string;
  apiBase: string;
}
interface VoiceProofPlugin {
  capabilities(): Promise<{ version: number }>;
  start(options: VoiceProofStart): Promise<VoiceProofSnapshot>;
  snapshot(): Promise<VoiceProofSnapshot>;
  end(options?: { runtimeId?: string }): Promise<void>;
  reset(options?: { runtimeId?: string }): Promise<void>;
  setMuted(options: { muted: boolean }): Promise<VoiceProofSnapshot>;
  sendUserMessage(options: { runtimeId: string; messageId: string; text: string }): Promise<{ messageId: string; status: VoiceMessageDelivery }>;
  messageReceipt(options: { runtimeId: string; messageId: string }): Promise<{ messageId: string; status: VoiceMessageDelivery }>;
  updateContext(options: { runtimeId: string; path: string; visible: boolean; feedback?: string; attachmentId?: string | null }): Promise<void>;
  /**
   * Version 4+. The app plays the connecting ring itself; see `ringing.ts`.
   * `by` only labels the app's report when iOS refuses the ring its audio
   * (MUCKA-IOS-P); Android has no such refusal and ignores it.
   */
  setRinging(options: { on: boolean; by?: RingCaller }): Promise<void>;
  /**
   * "Take a photo" with no glasses: the runtime asks the page to open the
   * phone's camera (`photoRequest`) and waits up to 80s for this answer.
   * Binaries built before 29 Oct 2026 never ask. See `phone-photo-for-call.ts`.
   */
  answerPhotoRequest(options: { runtimeId: string; requestId: string; attachmentId?: string; error?: 'CANCELLED' | 'FAILED' }): Promise<void>;
  addListener(event: 'photoRequest', listener: (e: { runtimeId: string; requestId: string }) => void): Promise<PluginListenerHandle>;
}
/** Who asked for the connecting ring. */
export type RingCaller = 'tap' | 'wake_word' | 'connecting';

export const VoiceProof = registerPlugin<VoiceProofPlugin>('MuckaVoiceProof');
export const hasVoiceProof = () => isNative() && hasNativePlugin('MuckaVoiceProof');

export async function stopNativeVoiceProof(): Promise<void> {
  if (hasVoiceProof()) await VoiceProof.reset();
}
